<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="show" class="glass-modal-overlay" @click.self="handleOverlayClick">
        <transition name="modal-slide">
          <div v-if="show" class="glass-modal" :class="[`glass-modal--${size}`]" :data-testid="testId || 'success-modal'">
            <!-- Header -->
            <div v-if="!hideHeader" class="glass-modal__header">
              <h3 class="glass-modal__title">
                <slot name="title">{{ title }}</slot>
              </h3>
              <button 
                v-if="showClose" 
                class="glass-modal__close" 
                @click="close"
                aria-label="Fermer"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            
            <!-- Body -->
            <div class="glass-modal__body" :class="{ 'glass-modal__body--no-padding': noPadding }">
              <slot></slot>
            </div>
            
            <!-- Footer -->
            <div v-if="$slots.footer" class="glass-modal__footer">
              <slot name="footer"></slot>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<script>
export default {
  name: 'GlassModal',
  emits: ['close', 'update:show'],
  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg', 'xl', 'full'].includes(value)
    },
    showClose: {
      type: Boolean,
      default: true
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    noPadding: {
      type: Boolean,
      default: false
    },
    closeOnOverlay: {
      type: Boolean,
      default: true
    },
    closeOnEscape: {
      type: Boolean,
      default: true
    },
    testId: {
      type: String,
      default: null
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
        if (this.closeOnEscape) {
          document.addEventListener('keydown', this.handleEscape);
        }
      } else {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.handleEscape);
      }
    }
  },
  beforeUnmount() {
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.handleEscape);
  },
  methods: {
    close() {
      this.$emit('close');
      this.$emit('update:show', false);
    },
    handleOverlayClick() {
      if (this.closeOnOverlay) {
        this.close();
      }
    },
    handleEscape(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    }
  }
};
</script>

<style scoped>
.glass-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.glass-modal {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 24px;
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Sizes */
.glass-modal--sm {
  width: 100%;
  max-width: 400px;
}

.glass-modal--md {
  width: 100%;
  max-width: 560px;
}

.glass-modal--lg {
  width: 100%;
  max-width: 800px;
}

.glass-modal--xl {
  width: 100%;
  max-width: 1140px;
}

.glass-modal--full {
  width: calc(100% - 2rem);
  height: calc(100% - 2rem);
  max-width: none;
  max-height: none;
}

/* Header */
.glass-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.glass-modal__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #424242;
}

.glass-modal__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #424242;
  transition: all 0.2s ease;
}

.glass-modal__close:hover {
  background: rgba(195, 66, 63, 0.1);
  color: #c3423f;
  transform: rotate(90deg);
}

/* Body */
.glass-modal__body {
  padding: 2rem;
  overflow-y: auto;
  flex: 1;
}

.glass-modal__body--no-padding {
  padding: 0;
}

/* Footer */
.glass-modal__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
}

/* Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: all 0.3s ease;
}

.modal-slide-enter,
.modal-slide-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-20px);
}

/* Responsive */
@media (max-width: 768px) {
  .glass-modal {
    border-radius: 16px;
    max-height: 85vh;
  }
  
  .glass-modal__header,
  .glass-modal__body,
  .glass-modal__footer {
    padding: 1rem 1.25rem;
  }
  
  .glass-modal__title {
    font-size: 1.125rem;
  }
}
</style>
